import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ReactNode, MouseEvent } from "react";

/** id -> slug map for all managers; slugs are stable so cache aggressively. */
export const useManagerSlugMap = () => {
  const { data } = useQuery({
    queryKey: ["manager-slug-map"],
    queryFn: async () => {
      const map = new Map<string, string>();
      const pageSize = 1000;
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("managers")
          .select("id, slug")
          .not("slug", "is", null)
          .range(from, from + pageSize - 1);
        if (error) break;
        (data || []).forEach((m) => m.slug && map.set(m.id, m.slug));
        if (!data || data.length < pageSize) break;
      }
      return map;
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
  return (id: string | null | undefined) => (id ? data?.get(id) ?? null : null);
};

interface ManagerLinkProps {
  managerId: string;
  slug?: string | null;
  className?: string;
  children: ReactNode;
  /** Used only when no slug is known yet */
  onFallbackClick?: () => void;
}

/** Crawlable link to a manager page; falls back to a button until the slug is known. */
export function ManagerLink({ managerId, slug, className, children, onFallbackClick }: ManagerLinkProps) {
  const getSlug = useManagerSlugMap();
  const resolved = slug || getSlug(managerId);
  const stop = (e: MouseEvent) => e.stopPropagation();
  if (resolved) {
    return (
      <Link to={`/manager/${resolved}`} className={className} onClick={stop}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        stop(e);
        onFallbackClick?.();
      }}
    >
      {children}
    </button>
  );
}
